// Phase 15c — STRIKZA Reports export (closes the "downloadable"
// acceptance criterion). Pure, deterministic serializers + a
// browser-only download trigger. No I/O, no fabrication: only fields
// that exist on the row are emitted, every artifact carries the
// honesty footer (MASTER Non-Neg #9 — never a promise / no public
// track record). Generation (writing report rows) remains infra-
// gated: it needs an INSERT RLS policy migration + a generator and
// is intentionally NOT done here.

import type { Database } from "../db/types.ts";

export type ReportRow = Database["public"]["Tables"]["reports"]["Row"];

const HONESTY_FOOTER =
  "STRIKZA does not publish a public track record, ROI, or win-rate claims. " +
  "This is planning intelligence, not a promise of any outcome.";

function jsonLines(label: string, value: unknown): string[] {
  if (value === null || value === undefined) return [];
  const arr = Array.isArray(value) ? value : [value];
  if (arr.length === 0) return [];
  const out = [`${label}:`];
  for (const item of arr) {
    const s = typeof item === "string" ? item : JSON.stringify(item);
    out.push(`  - ${s}`);
  }
  return out;
}

/** Deterministic plain-text artifact for a single report. Only
 *  present fields are rendered; nothing is invented. */
export function serializeReportText(row: ReportRow): string {
  const lines: string[] = [
    "STRIKZA REPORT",
    "==============",
    `Type:    ${row.report_type}`,
    `Title:   ${row.title}`,
    `Created: ${row.created_at}`,
    `Status:  ${row.status}`,
  ];
  if (typeof row.accuracy === "number") {
    lines.push(`Accuracy:   ${row.accuracy}`);
  }
  if (typeof row.confidence === "number") {
    lines.push(`Confidence: ${row.confidence}`);
  }
  if (row.summary) {
    lines.push("", "Summary:", row.summary);
  }
  const metrics = jsonLines("Metrics", row.metrics);
  if (metrics.length > 0) lines.push("", ...metrics);
  const reasoning = jsonLines("Reasoning", row.reasoning);
  if (reasoning.length > 0) lines.push("", ...reasoning);
  lines.push("", "—", HONESTY_FOOTER);
  return lines.join("\n");
}

function csvCell(v: unknown): string {
  const s = v === null || v === undefined
    ? ""
    : typeof v === "string"
      ? v
      : JSON.stringify(v);
  return `"${s.replace(/"/g, '""')}"`;
}

const CSV_HEADER =
  "report_type,title,created_at,status,accuracy,confidence,summary";

/** Deterministic CSV for one-or-many reports. Header always present
 *  so an empty list still yields a valid, honest file. */
export function serializeReportsCsv(rows: readonly ReportRow[]): string {
  const body = rows.map((r) =>
    [
      r.report_type,
      r.title,
      r.created_at,
      r.status,
      r.accuracy,
      r.confidence,
      r.summary,
    ].map(csvCell).join(",")
  );
  return [CSV_HEADER, ...body, `# ${HONESTY_FOOTER}`].join("\n");
}

function triggerDownload(
  content: string,
  filename: string,
  mime: string,
): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Browser-only. SSR-safe (no-op without document). */
export function downloadReportText(row: ReportRow): void {
  triggerDownload(
    serializeReportText(row),
    `strikza-report-${row.report_type}-${row.id}.txt`,
    "text/plain",
  );
}

export function downloadReportsCsv(rows: readonly ReportRow[]): void {
  triggerDownload(
    serializeReportsCsv(rows),
    "strikza-reports.csv",
    "text/csv",
  );
}
